import { atomWithReducer, loadable } from "jotai/utils";
import { PrimitiveAtom, WritableAtom, atom } from "jotai";
import { CmsData } from "../types/cms";
import {
  Account,
  BookmakerDisplayBestOdds,
  BookmakerDisplayPreferred,
  RunnerSortOptionValue,
  User,
  UserPriceFormat,
  UserState,
} from "../types/user";
import { Breadcrumb } from "../types/breadcrumb";
import { AdOverlay, AdvertDetails, Placements } from "../types/advert";
import { Channel } from "../components/Screens/Watch/Live/LiveStreamSection";
import { getLiveChannelsInfo } from "../client/watch/live";
import { Preroll } from "../types/replay";
import { ArticleSlugId } from "../types/news/article";
import { NotificationsSection } from "../hooks/useNotifications";
import { Meetings, RaceType } from "../types/resultedRaces";
import { Plan } from "../types/memberPlans";
import {
  MENU,
  MORE_MENU,
  MenuItem,
  MoreMenuItem,
} from "../components/Header/menu_types";
import { Cable } from "@rails/actioncable";
import { PageRes } from "../types/page";
import { LayoutRectangle, ScrollView, View } from "react-native";
import { DeviceType } from "../hooks/useDeviceType";
import { Nav } from "../client/more/externalWebsites";
import { RaceReplay } from "../components/Screens/Watch/Replays/MeetingsDropdown";
import { PriceFormat } from "../types/tipster";
import {
  OddsFilterOption,
  OddsFilterOptionValue,
  PriceFormatOption,
  RunnerSortOption,
} from "../components/Tabs/Sorter";
import {
  updateLocalStorageUser,
  updateMemberPreferences,
} from "../client/account";

export type AuthModalType =
  | "login"
  | "join"
  | "upgrade"
  | "fdp"
  | "activate"
  | "ios_paid"
  | Plan
  | {
      id: number;
      name: string;
      cost: string;
      form_heading: string | undefined;
      form_sub_text: string | undefined;
      offer_slug?: string;
    }
  | false;

export type GlobalPlayerSourceType = {
  source: string;
  preroll?: Preroll;
  getNewSourceAfterAd?: () => Promise<{
    source: string;
    clips?: RaceReplay["clips"];
  }>;
  clips?: RaceReplay["clips"];
};

export const refreshStreamCountAtom = atom<number>(0);

export const openAuthModalAtom = atom<AuthModalType>(
  false
) as PrimitiveAtom<AuthModalType>;

export const openDeviceManagementModalAtom = atom<boolean>(false);

// Domain Atom for Runner Sorter
export const runnerSortPreferenceAtom: SimpleWriteAtom<RunnerSortOptionValue> =
  atom(
    // READ — return the string union stored in the user object
    (get) => {
      const user = get(userAtom);
      return user?.member?.default_sort || "cloth_number";
    },

    // WRITE — update via userUpdateAtom
    (_get, set, nextValue) => {
      set(userUpdateAtom, (user) => ({
        ...user,
        member: {
          ...user.member,
          default_sort: nextValue,
        },
      }));
    }
  );

export const sortOptions: RunnerSortOption[] = [
  { text: "Price", value: "price" },
  { text: "Number", value: "cloth_number" },
  { text: "TF Rating", value: "tf_rating" },
  { text: "Official Rating", value: "official_rating" },
];

// A typed helper for writable atoms whose write function
// accepts a single update value.
type SimpleWriteAtom<Value> = WritableAtom<Value, [Value], void>;

// Matching UI Atom for Runner Sorter
export const runnerSortOptionAtom = atom(
  // READ — convert stored value → full option object
  (get): RunnerSortOption => {
    const storedValue = get(runnerSortPreferenceAtom);

    return sortOptions.find((opt) => opt.value === storedValue);
  },

  // WRITE — store only the .value (the union type) on the user
  (_get, set, nextOption: RunnerSortOption) => {
    set(runnerSortPreferenceAtom, nextOption.value);
  }
);

export const preferenceFormat = {
  fractions: "fractional" as PriceFormat,
  fractional: "fractional" as PriceFormat,
  decimals: "decimal" as PriceFormat,
  decimal: "decimal" as PriceFormat,
};

// Example of derived atom - which means less state to track
// the same thing. which overall means:
//   - less state updates
//   - less re-rendering
//   - single source of truth (userAtom)
// We'll be using this more throughout the codebase.
export const priceFormatAtom: SimpleWriteAtom<UserPriceFormat> = atom(
  (get) => {
    // get users odds display or return default
    const user = get(userAtom);

    return preferenceFormat[user?.member?.odds_display] ?? "fractional";
  },
  (get, set, nextValue) => {
    set(userUpdateAtom, (user) => ({
      ...user,
      member: {
        ...user.member,
        odds_display: nextValue,
      },
    }));
  }
);

export const priceFormatOptionAtom = atom<PriceFormatOption, PriceFormatOption>(
  (get) => {
    const priceFormat = get(priceFormatAtom);

    return { text: priceFormat.capitalize(), value: priceFormat };
  },
  (get, set, nextValue) => {
    set(
      priceFormatAtom,
      nextValue.value === "decimal" ? "decimals" : "fractional"
    );
  }
);

export const bookmakerPreferenceAtom = atom<
  BookmakerDisplayPreferred | BookmakerDisplayBestOdds | undefined,
  OddsFilterOptionValue
>(
  (get) => {
    const user = get(userAtom);

    return (
      user?.member?.bookmaker_display || {
        best_odds: true,
      }
    );
  },

  // WRITE: convert OddsFilterOptionValue → updated User via userUpdateAtom
  (_get, set, nextValue) => {
    console.log("received set for bookmakerPreferenceAtom:", nextValue);

    set(userUpdateAtom, (user) => {
      if (nextValue === "preferred-bookmaker") {
        return {
          ...user,
          member: {
            ...user.member,
            bookmaker_display: {
              preferred_bookmaker: true,
              bookmaker_id: 8, // TODO
            },
          },
        };
      } else {
        // "best-odds"
        return {
          ...user,
          member: {
            ...user.member,
            bookmaker_display: {
              best_odds: true,
            },
          },
        };
      }
    });
  }
);

export const oddsFilterAtom = atom<OddsFilterOption, OddsFilterOptionValue>(
  (get) => {
    const preference = get(bookmakerPreferenceAtom);

    const preferredBookmaker = (
      preference as BookmakerDisplayPreferred | undefined
    )?.preferred_bookmaker;

    return preferredBookmaker
      ? {
          text: "Preferred Bookmaker",
          value: "preferred-bookmaker" as const,
        }
      : { text: "Best Odds", value: "best-odds" as const };
  },

  // WRITE — just forward `nextValue` to the domain atom
  (_, set, nextValue) => {
    console.log("received set for oddsFilterAtom:", nextValue);
    // nextValue is either "best-odds" or "preferred-bookmaker"
    set(bookmakerPreferenceAtom, nextValue);
  }
);

export const preferredBookmakerAtom = atom<number | null>(
  (get) => {
    const preference = get(bookmakerPreferenceAtom);

    return (
      (preference as BookmakerDisplayPreferred | undefined)?.bookmaker_id ||
      null
    );
  },
  (get, set, nextValue: number) => {
    if (!nextValue) return;
    set(userUpdateAtom, (user) => ({
      ...user,
      member: {
        ...user.member,
        bookmaker_display: {
          preferred_bookmaker: true,
          bookmaker_id: nextValue,
        },
        bookmaker_id: nextValue,
      },
    }));
  }
);

export const userAtom = atom<User>(null) as PrimitiveAtom<User>;
export const userStateAtom = atom<User | UserState>((get) => {
  const user = get(userAtom);

  if (user && "free_account" in user) {
    if (user.free_account) return "free_account";
    return "paid_account";
  }
  return "anonymous";
});

export const userAccountAtom = atom<Account | null>((get) => {
  const user = get(userAtom);
  if (user && "free_account" in user) {
    return {
      detail: user.detail,
      free_account: user.free_account,
      total_cost: user.total_cost,
      frequency: user.frequency,
      address: user.address,
    } as Account;
  } else {
    return null;
  }
});

export const userUpdateAtom = atom(
  null,
  async (get, set, next: User | ((prev: User) => User)) => {
    const prev = get(userAtom); // User | null
    const isLoggedIn = get(isLoggedInAtom);

    console.log("received prev for userUpdateAtom:", prev);

    const updatedUser =
      typeof next === "function" ? (next as (u: User) => User)(prev) : next;

    console.log("resolved updatedUser:", updatedUser);

    set(userAtom, updatedUser);

    // persist
    await updateLocalStorageUser({ updatedUser, anon: !isLoggedIn });
    // const isLoggedIn = get(isLoggedInAtom);
    // only sync real users to backend
    // if updatedUser.detail?.email exists updatePref
    if (isLoggedIn) {
      updateMemberPreferences(updatedUser);
    }
  }
);

export const showTrackerOnRacecardAtom = atom<boolean>(
  (get) => {
    const user = get(userAtom);

    return user?.member?.tracker_display || false;
  },
  (get, set, nextValue) => {
    console.log("received set for showTrackerOnRacecardAtom:", nextValue);
    set(userUpdateAtom, (user) => ({
      ...user,
      member: {
        ...user.member,
        tracker_display: nextValue,
      },
    }));
  }
);

// TODO - this needs to actually come from backend
// otherwise the sync will be lost between devices.
// Also - need to add it to the type set.
export const showRiqDataAtom = atom<boolean>(
  (get) => {
    const user = get(userAtom);

    return user?.member?.riq_display || false;
  },
  (get, set, nextValue) => {
    console.log("received set for showRiqDataAtom:", nextValue);
    set(userUpdateAtom, (user) => ({
      ...user,
      member: {
        ...user.member,
        riq_display: nextValue,
      },
    }));
  }
);

export const showOddsCheckerOnRacecardAtom = atom<boolean>(
  (get) => {
    const user = get(userAtom);
    return user?.member?.oddschecker_display || false;
  },

  (get, set, nextValue) => {
    console.log("received set for showOddsCheckerOnRacecardAtom:", nextValue);
    set(userUpdateAtom, (user) => ({
      ...user,
      member: {
        ...user.member,
        oddschecker_display: nextValue,
      },
    }));
  }
);

export const showFormOnRacecardAtom = atom<boolean>(
  (get) => {
    const user = get(userAtom);
    return user?.member?.form_display || false;
  },
  (get, set, nextValue) => {
    console.log("received set for showFormOnRacecardAtom:", nextValue);
    set(userUpdateAtom, (user) => ({
      ...user,
      member: {
        ...user.member,
        form_display: nextValue,
      },
    }));
  }
);

export const showTimeformOnRacecardAtom = atom<boolean>(
  (get) => {
    const user = get(userAtom);
    return user?.member?.timeform_display || false;
  },
  (get, set, nextValue) => {
    console.log("received set for showTimeformOnRacecardAtom:", nextValue);
    set(userUpdateAtom, (user) => ({
      ...user,
      member: {
        ...user.member,
        timeform_display: nextValue,
      },
    }));
  }
);

// Use this to check if user is logged in
// This basically checks for existence of email on user detail
// because that guarantees an account.

// Checking for just user would also be true for anon users.
export const isLoggedInAtom = atom<boolean>((get) => {
  const user = get(userAtom) as User;
  if (!user) return false;

  if ("detail" in user) {
    return !!user?.detail?.email;
  }
  return false;
});

export const pageTitleAtom = atom<string>("");
export const scrollEnabledAtom = atom<boolean>(true);
export const openDrawerAtom = atom<boolean>(false);
export const globalFullscreenAtom = atom<boolean>(false);
export const pageDataAtom = atom<PageRes | undefined>({
  page: { title: "", content: "", content_sections: [] },
});
export const autoMetaDescriptionAtom = atom<string>("");
export const breadcrumbsAtom = atom<Breadcrumb[]>([]);
export const headerEndYAtom = atom<number>(0);
export const openCalendarAtom = atom<boolean>(false);
export const maxDateAtom = atom<boolean>(false);
export const minDateAtom = atom<boolean>(false);
export const cmsDataAtom = atom<CmsData>({} as CmsData);
export const externalWebsitesAtom = atom<Nav[]>([]);
export const menuAtom = atom<MenuItem[]>(MENU);
export const moreMenuAtom = atom<MoreMenuItem[]>(MORE_MENU);
export const menuItemWidthAtom = atom<number>(0);
export const routeAndPathAtom = atom<{ route: string; path: string }>({
  route: "",
  path: "",
});
export const changeDetailsModalAtom = atom<boolean>(false);
export const updatedHorseTrackerAtom = atom<boolean>(false);
export const selectedPlanAtom = atom<number | undefined>(undefined);
export const advertisingAtom = atom<Placements>({} as Placements);
export const advertOverlayModalAtom = atom<AdOverlay>({} as AdOverlay);
export const seenOverlayAdvertsAtom = atom<number[]>([]);
export const seenTakeoverAdvertsAtom = atom<number[]>([]);
export const showSkyScraperAdAtom = atom<boolean>(true);

const asyncLiveChannelsAtom = atom<Promise<Channel[]>>(
  async () => await getLiveChannelsInfo()
);
export const loadableLiveChannelsAtom = loadable(asyncLiveChannelsAtom);
export const selectedChannelsAtom = atom<Channel[]>([]);
export const liveStreamFullScreenAtom = atom<boolean>(false);
export const globalPlayerSourceAtom = atom<GlobalPlayerSourceType>({
  source: "",
});
export const playerPIDFocusedAtom = atom<string>("");
export const articlesForNavigationAtom = atom<ArticleSlugId[]>([]);
export const unreadNotificationCountAtom = atom<number>(0);
export const notificationsAtom = atom<NotificationsSection[]>([]);
export const meetingsToFilterAtom = atom<Meetings>([]);
export const selectedMeetingAtom = atom<string | undefined>(undefined);
export const showPreferenceHeaderAtom = atom<boolean>(false);
export const updateTrackerPreferencesAtom = atom<boolean>(false);
export const updateAccountPreferencesAtom = atom<boolean>(false);

type PreferencesButton = {
  lastAction?: "SAVE" | "RESET" | "CANCEL" | undefined;
};

const initialPreferencesButton: PreferencesButton = {
  lastAction: undefined,
};

const preferencesButtonReducer = (
  state: PreferencesButton,
  action: { type: "SAVE" | "RESET" | "CANCEL" }
) => {
  return { ...state, lastAction: action.type };
};

export const preferencesButtonReducerAtom = atomWithReducer(
  initialPreferencesButton,
  preferencesButtonReducer
);

export const displayCancelBtnAtom = atom<boolean>(false);
export const appScrollRefAtom = atom<React.RefObject<ScrollView> | undefined>(
  undefined
);
export const appScrollRefAtom2 = atom<React.RefObject<ScrollView> | undefined>(
  undefined
);
export const openRaceMatrixAtom = atom<boolean>(false);
export const scriptsAtom = atom<string[]>([]);
export const headerHeightAtom = atom<number>(0);
export const tabBarHeightAtom = atom<number>(0);

export const OnspaceCableAtom = atom<Cable>({} as Cable);

export const tabAdLayoutAtom = atom<LayoutRectangle>({
  height: 0,
  width: 0,
  x: 0,
  y: 0,
});
export const tabLayoutAtom = atom<any>(undefined);
export const raceTypeAtom = atom<RaceType>("Flat");

type FloatingPopup = {
  visible: boolean;
  content: React.ReactNode;
  reference: React.RefObject<any>;
};

export const floatingPopupAtom = atom<FloatingPopup>({
  visible: false,
  content: null,
  reference: null,
});

export const topOfPageRefAtom = atom<View | null>(undefined);
export const tutorialStepIndexAtom = atom<number | undefined>(undefined);
export const TOOLTIP_HEIGHT = 250;
export type ResultsTutorials =
  | "full"
  | "results"
  | "raceIqComparison"
  | "sectionals";
export const runningTutorialAtom = atom<ResultsTutorials>(undefined);

export const signupPageNumberAtom = atom<string>("");
export const appHeightAndWidthAtom = atom({
  appHeight: 0,
  appWidth: 0,
});
export const displayedPlanAtom = atom<Plan | undefined>(undefined);
export const deviceTypeAtom = atom<DeviceType>("UNKNOWN");
